import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useCallbackRef } from './use-callback-ref.js';

/**
 * https://github.com/jaredLunde/react-hook/blob/master/packages/debounce/README.md
 * @param callback This is the callback you want to debounce. You need to wrap closures/unstable callbacks in useCallback() so that they are stable, otherwise throttling will break between renders.
 * @param wait Defines the amount of time you want setState to wait after the last received action before executing
 * @param leading Calls setState on the leading edge (right away). When false, setState will not be called until the next frame is due
 * @param tailing Specify invoking on the trailing edge of the timeout.
 * @returns A debounced version of your callback
 */
export function useDebounceCallback<T extends (...args) => unknown>(
  callback: T | undefined,
  wait = 100,
  leading = false,
  trailing = true
): T {
  const storedCallback = useCallbackRef<T>(callback);
  const timeout = useRef<ReturnType<typeof setTimeout>>();

  // Cleans up pending timeouts when the deps change
  useEffect(
    () => () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
      timeout.current = undefined;
    },
    [wait, leading, storedCallback]
  );

  return useCallback(
    ((...args) => {
      const { current } = timeout;
      // Calls on leading edge
      if (current === undefined && leading) {
        timeout.current = setTimeout(() => {
          timeout.current = undefined;
        }, wait);
        return storedCallback(...args);
      }
      // Clear the timeout every call and start waiting again
      if (current) {
        clearTimeout(current);
      }
      // Waits for `wait` before invoking the callback
      timeout.current = setTimeout(() => {
        timeout.current = undefined;
        // Calls on tailing edge
        if (trailing) {
          storedCallback(...args);
        }
      }, wait);
    }) as T,
    [leading, storedCallback, trailing, wait]
  );
}

/**
 * A hook that acts just like React.useState, but with a setState function that is only invoked after the wait time in ms has been exceeded between calls.
 * @param initialState The initial state provided to React.useState
 * @param wait The amount of time in ms you want to wait after the latest call before setting a new state.
 * @param leading Calls setState on the leading edge (right away). When false, setState will not be called until the next frame is due
 * @returns [state, setState]
 */
export const useDebounce = <State>(
  initialState: State | (() => State),
  wait?: number,
  leading?: boolean
): [State, Dispatch<SetStateAction<State>>] => {
  const state = useState(initialState);
  return [state[0], useDebounceCallback(state[1], wait, leading)];
};

/**
 * Void multiple clicks for clickable elements.
 * @param callback This is the callback you want to debounce. You need to wrap closures/unstable callbacks in useCallback() so that they are stable, otherwise throttling will break between renders.
 * @param wait Defines the amount of time you want setState to wait after the last received action before executing
 * @returns
 */
export const useDebounceClick = (callback: (...args) => void, wait = 500) => {
  return useDebounceCallback(callback, wait, true, false);
};
